import {BusinessAccountDto} from "../../services/auth/auth.service";
import {CreatorResponseDto} from "../../services/creator/creator.service";
import {FilterItemDto} from "../../shared/models/misc";

export interface CreateDiscussionDto {
  title: string;
  description: string;
  createdByUserId: string;
  createdByCreatorId: string | null;
  image: string;
  isPublic: boolean;
  businessAccountId: string;
}

export interface DiscussionResponseDto extends CreateDiscussionDto {
  id: string;
  createdDate: Date;
  userCreated: any;
  creatorCreated: any;
  businessAccount: any;
  comments?: DiscussionCommentResponse[]
}

export interface CreateDiscussionCommentDto {
  comment: string;
  createdByUserId: string | null;
  createdByCreatorId: string | null;
  discussionId: string;
}

export interface DiscussionCommentResponse extends CreateDiscussionCommentDto {
  id: string;
  createdDate: Date;
  upvotes: number;
  userCreated: any;
  creatorCreated: any;
  replies: DiscussionReplyResponse[]
}

export interface CreateDiscussionReplyDto {
  reply: string;
  createdByUserId: string;
  createdByCreatorId: string | null;
  commentId: string;
}

export interface DiscussionReplyResponse extends CreateDiscussionReplyDto {
  id: string;
  createdDate: Date;
  upvotes: number;
  userCreated: any;
  creatorCreated: any;
}

export interface PlusInviteDto {
  businessAccount?: BusinessAccountDto;
  businessAccountId: string;
  createdDate?: Date;
  creator?: CreatorResponseDto;
  creatorId?: string;
  email?: string;
  id?: string;
  invitedBy?: string;
  invitedById?: string;
  isDeleted?: boolean;
  mobile?: string;
  plusInvite?: any;
  plusInviteId?: string;
  status?: PlusInviteStatusEnum;
}

export enum PlusInviteStatusEnum {
  Invited = 'Invited',
  Accepted = 'Accepted',
  Applied = 'Applied',
  Declined = 'Declined'
}

export interface PlusInviteFilter {
  status?: PlusInviteStatusEnum[];
  businessAccountId: string;
  take: number;
  skip: number;
}

export interface ShoutPlusData {
  count: number;
  countApplied: number;
  countAccepted: number;
  countDeclined: number;
  countInvited: number;
  data: PlusInviteDto[]
}

export enum ShoutPlusBusinessCreatorStatusEnum {
  APPROVED = 'approved',
  /** Joined / applied; awaiting brand (manual) review — not eligible for briefs yet. */
  AWAITING_REVIEW = 'awaiting_review',
  BLOCKED = 'blocked',
  PENDING_APPROVAL = 'pending_approval',
}

/** API may send legacy casing; canonical values are lowercase with underscores. */
export function normalizeShoutPlusBusinessCreatorStatus(
  status: string | undefined | null,
): ShoutPlusBusinessCreatorStatusEnum | null {
  if (status == null || String(status).trim() === '') {
    return null;
  }
  const key = String(status).trim().toLowerCase().replace(/-/g, '_');
  // Legacy DB / clients used `active` for the same state.
  if (key === 'active') {
    return ShoutPlusBusinessCreatorStatusEnum.AWAITING_REVIEW;
  }
  // Legacy status string before rename to pending_approval.
  if (key === 'pending_approve') {
    return ShoutPlusBusinessCreatorStatusEnum.PENDING_APPROVAL;
  }
  const values = Object.values(ShoutPlusBusinessCreatorStatusEnum) as string[];
  return values.includes(key) ? (key as ShoutPlusBusinessCreatorStatusEnum) : null;
}

/** Pill/table label: lowercase words, underscores → spaces. */
export function formatShoutPlusBusinessCreatorStatusLabel(status: string | undefined | null): string {
  if (status == null || String(status).trim() === '') {
    return '';
  }
  const n = normalizeShoutPlusBusinessCreatorStatus(status);
  if (n === ShoutPlusBusinessCreatorStatusEnum.AWAITING_REVIEW) {
    return 'Awaiting review';
  }
  if (n === ShoutPlusBusinessCreatorStatusEnum.PENDING_APPROVAL) {
    return 'Pending approval';
  }
  const base = n ?? String(status).trim().toLowerCase().replace(/-/g, '_');
  return base.replace(/_/g, ' ');
}

const SHOUT_PLUS_STATUS_PILL_CLASS: Record<ShoutPlusBusinessCreatorStatusEnum, string> = {
  [ShoutPlusBusinessCreatorStatusEnum.AWAITING_REVIEW]: 'pill-indigo',
  [ShoutPlusBusinessCreatorStatusEnum.APPROVED]: 'pill-teal',
  [ShoutPlusBusinessCreatorStatusEnum.BLOCKED]: 'pill-rose',
  [ShoutPlusBusinessCreatorStatusEnum.PENDING_APPROVAL]: 'pill-amber',
};

export function shoutPlusBusinessCreatorStatusPillClass(
  status: string | undefined | null,
): string {
  const normalized = normalizeShoutPlusBusinessCreatorStatus(status);
  return normalized ? SHOUT_PLUS_STATUS_PILL_CLASS[normalized] ?? '' : '';
}

export const shoutPlusStatusList: FilterItemDto[] = [
  {id: 1, active: true, name: 'All', value: 'all', icon: {name: '', type: 'fas'}, class: 'bg-red-400 hover:bg-red-500 text-white'},
  {
    id: 2,
    active: true,
    name: 'Awaiting review',
    value: ShoutPlusBusinessCreatorStatusEnum.AWAITING_REVIEW,
    icon: {name: '', type: 'fas'},
    class: 'bg-blue-400 hover:bg-blue-500 text-white'
  },
  {
    id: 3,
    active: true,
    name: 'Approved',
    value: ShoutPlusBusinessCreatorStatusEnum.APPROVED,
    icon: {name: '', type: 'fas'},
    class: 'bg-blue-400 hover:bg-blue-500 text-white'
  },
  {
    id: 4,
    active: false,
    name: 'Blocked',
    value: ShoutPlusBusinessCreatorStatusEnum.BLOCKED,
    icon: {name: '', type: 'fas'},
    class: 'bg-blue-400 hover:bg-blue-500 text-white'
  },
  {
    id: 5,
    active: false,
    name: 'Pending approval',
    value: ShoutPlusBusinessCreatorStatusEnum.PENDING_APPROVAL,

    icon: {name: '', type: 'fas'},
    class: 'bg-amber-400 hover:bg-amber-500 text-white'
  },
];

export interface ShoutPlusBusinessTier {
  /**
   * Unique identifier for the tier (UUID).
   */
  id: string;

  /**
   * Stable identifier such as 'PLUS_1K', 'PLUS_2K', 'ENTERPRISE_50K'.
   */
  code: string;

  /**
   * Owning business account for this tier definition.
   */
  business_account_id: string;
  businessAccount?: BusinessAccountDto;

  /**
   * Position of this tier for the business (1–5).
   */
  tier_index: number;

  /**
   * Human-readable name, e.g. 'Plus 1K'.
   */
  name: string;

  /**
   * Maximum number of connected creator accounts for this plan.
   * Null means 'no upper bound' (e.g. Enterprise 50K+).
   */
  max_connected_accounts: number | null;

  /**
   * Monthly fee in minor units for this plan.
   * Example: £3,000 => 300000 (GBP minor units).
   */
  monthly_fee: string;

  /**
   * Effective price per connected account in minor units.
   * Example: £3.00 => 300 (GBP minor units).
   */
  price_per_account: string;

  /**
   * ISO currency code for this tier's pricing (e.g. 'GBP', 'USD').
   */
  currency_code: string;

  /**
   * Soft enable/disable flag for retiring legacy tiers.
   */
  is_active: boolean;
}
